import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { pageNumbers } from '../../search-page/search-page';
// the pagination styles live with the search results
import '../../search-page/search-page.css';

export const Check = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
);

// renders its children next to the page title, in the slot the panel provides
export const PanelActions = ({ children }) => {
    const { actionsSlot } = useOutletContext() || {};
    return actionsSlot ? createPortal(children, actionsSlot) : null;
};

// `danger` marks actions that remove or block something
export const PanelConfirm = ({ title, message, confirmLabel, danger = false, onConfirm, onClose }) => {
    const { t } = useTranslation(["common"]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return createPortal(
        <div className='hs hs-panel-confirm-overlay' onClick={onClose}>
            <div className='hs-panel-confirm' role='alertdialog' aria-label={title} onClick={(e) => e.stopPropagation()}>
                <span className={`hs-panel-confirm-icon ${danger ? 'danger' : ''}`}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
                </span>
                <h2>{title}</h2>
                <p>{message}</p>
                <div className='hs-panel-confirm-actions'>
                    <button className='hs-panel-outline' onClick={onClose}>{t('cancel')}</button>
                    <button autoFocus className={`hs-panel-primary ${danger ? 'danger' : ''}`} onClick={() => { onClose(); onConfirm(); }}>{confirmLabel || t('confirm')}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export const PanelPagination =({ page, pageCount, onChange }) => {
    if (pageCount <= 1) return null;
    const goToPage = (n) => {
        onChange(n);
        window.scrollTo(0, 0);
    };
    return (
        <div className='hs-pagination'>
            <span></span>
            <div className='hs-pagination-pages'>
                <button className='arrow' disabled={page <= 1} onClick={() => goToPage(page - 1)}>←</button>
                {pageNumbers(page, pageCount).map(n => (
                    <button key={n} className={n === page ? 'on' : ''} onClick={() => goToPage(n)}>{n}</button>
                ))}
                <button className='arrow' disabled={page >= pageCount} onClick={() => goToPage(page + 1)}>→</button>
            </div>
        </div>
    );
};

export const Field =({ label, ...input }) => (
    <label className='hs-panel-field'>
        <span className='hs-panel-field-label'>{label}</span>
        <input {...input} />
    </label>
);

export const Toggle = ({ on, onChange, label }) => (
    <button className={`hs-panel-toggle ${on ? 'on' : ''}`} role='switch' aria-checked={on} aria-label={label}
        onClick={() => onChange(!on)}><span></span></button>
);

// options are { id, label }
export const SelectDropdown = ({ label, value, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const selected = options.find(o => String(o.id) === String(value));

    return (
        <div className='hs-panel-select' ref={ref}>
            <button className={`hs-panel-field hs-panel-select-trigger ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
                <span className='hs-panel-field-label'>{label}</span>
                <span className='hs-panel-select-value'>
                    <span>{selected?.label}</span>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5 6 6.5 11 1.5" stroke="#5B6779" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
            </button>
            {open && (
                <div className='hs-panel-pop hs-panel-select-pop'>
                    {options.map(o => (
                        <button key={`option-${o.id}`} className={`hs-panel-pop-item ${o === selected ? 'on' : ''}`}
                            onClick={() => { onChange(o.id); setOpen(false); }}>
                            <span className='hs-panel-pop-label'>{o.label}</span>
                            {o === selected && <Check />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
